import type { GQL_ID } from '@@partner:types:graphql'
import React, { useCallback, useEffect, useMemo } from 'react'
import { mergeLeft } from 'merge-left-utils'
import {
  makeDefaultState,
  mapApiDataToState,
  mapStateToApiVariable
} from './SlotFormState'
import {
  makeGraphQLErrorNotification,
  notifyOk
} from '@@partner:helpers/notification'
import { useValidatableForm } from '@@partner:helpers/hooks/rsuite'
import { useFetchSingleSlot } from '@@partner:graphql/query/shop/singleSlot.gql'
import { useMutateUpdateShopSlotInfo } from '@@partner:graphql/mutation/shop/updateShopSlotInfo.gql'

import ValidationModel from '../validation/SlotFormValidationModel'

import { Button, ButtonToolbar, DatePicker, Form, InputNumber, Progress, Tag } from 'rsuite'
import AppLoader from '@components/common/AppLoader'
import GraphQLCommonErrorDisplay from '@components/common/GraphQLCommonErrorDisplay'

import { RSUITE_DATEPICKER_LOCALE } from '@@partner:config/rsuite'

import './ShopSlotForm.scss'

export interface SlotFormProps {
  slotId: GQL_ID
  onSuccess?: () => void
  onCancel?: () => void
}

export default function ShopSlotForm ({ slotId, onSuccess, onCancel }: SlotFormProps) {
  const { formState, setFormState, setFormRef, setFormErrors } =
    useValidatableForm(() => makeDefaultState(slotId))

  const { slot, loading, error } = useFetchSingleSlot({
    slotId
  })

  const [
    updateSlotInfo,
    { loading: loadingUpdateSlotInfo, error: errorUpdatingSlotInfo }
  ] = useMutateUpdateShopSlotInfo()

  useEffect(() => {
    if (!loading && slot) {
      setFormState((state) => mergeLeft(state, mapApiDataToState(slot)))
    }
  }, [ slot, loading, setFormState ])

  const handleFormChange = useCallback(
    (formData: any) => {
      setFormState((state) => mergeLeft(state, formData))
    },
    [ setFormState ]
  )

  const handleTimeChange = useCallback(
    (field: string) => (date: Date) => {
      setFormState((state) => mergeLeft(state, { [field]: date.getTime() }))
    },
    [ setFormState ]
  )

  const handleFormError = useCallback(
    (error: any) => {
      setFormErrors(error)
    },
    [ setFormErrors ]
  )

  const handleFormSubmit = useCallback(
    (isOk = false) => {
      if (isOk) {
        updateSlotInfo({
          variables: {
            slotInfo: mapStateToApiVariable(formState),
            upsert: true
          }
        })
          .then(({ data }) => {
            notifyOk('Слот обновлен')
            setFormState((state) => mergeLeft(state, data?.slot))
            onSuccess?.()
          })
          .catch(makeGraphQLErrorNotification('Ошибка сохранения данных'))
      }
    },
    [ formState, updateSlotInfo, setFormState, onSuccess ]
  )

  const ordersPercent = useMemo(() => {
    if (!slot || !slot.orders_capacity) return 0
    return Math.round((slot.orders_count / slot.orders_capacity) * 100)
  }, [ slot ])

  const positionsPercent = useMemo(() => {
    if (!slot || !slot.position_capacity) return 0
    return Math.round((slot.positions_count / slot.position_capacity) * 100)
  }, [ slot ])

  if (error) {
    return (
      <GraphQLCommonErrorDisplay
        error={error}
        title="Ошибка загрузки данных слота"
      />
    )
  }

  const tzOffset = slot?.shop?.tzOffset ?? slot?.deliveryZone?.tzOffset ?? 0
  const timezone = tzOffset / 3600
  const ownerName = slot?.shop?.name ?? slot?.deliveryZone?.name ?? ''
  const ownerLabel = slot?.shop ? 'Магазин' : 'Зона доставки'
  const tzLabel = timezone >= 0 ? `GMT+${timezone}` : `GMT${timezone}`

  return (
    <Form
      fluid
      model={ValidationModel}
      formValue={formState}
      onChange={handleFormChange}
      onSubmit={handleFormSubmit}
      onCheck={handleFormError}
      ref={setFormRef}
      className="SlotEditForm"
    >
      {loading && <AppLoader.Inner />}

      {errorUpdatingSlotInfo && (
        <GraphQLCommonErrorDisplay
          title={'Не удалось сохранить данные слота'}
          error={errorUpdatingSlotInfo}
        />
      )}

      {/* Информация о владельце и часовом поясе */}
      {ownerName && (
        <div className="SlotEditForm__header">
          <Tag color="blue" size="sm">{ownerLabel}</Tag>
          <span className="SlotEditForm__owner-name">{ownerName}</span>
          <Tag color="cyan" size="sm">{tzLabel}</Tag>
        </div>
      )}

      {/* Заполненность слота */}
      {slot && (
        <div className="SlotEditForm__stats">
          <div className="SlotEditForm__stat">
            <div className="SlotEditForm__stat-label">
              Заказы: {slot.orders_count} / {slot.orders_capacity}
            </div>
            <Progress.Line
              percent={ordersPercent}
              strokeColor={ordersPercent > 90 ? '#f44336' : ordersPercent > 70 ? '#ff9800' : '#4caf50'}
              showInfo={false}
              className="SlotEditForm__progress"
            />
          </div>
          <div className="SlotEditForm__stat">
            <div className="SlotEditForm__stat-label">
              Позиции: {slot.positions_count} / {slot.position_capacity}
            </div>
            <Progress.Line
              percent={positionsPercent}
              strokeColor={positionsPercent > 90 ? '#f44336' : positionsPercent > 70 ? '#ff9800' : '#4caf50'}
              showInfo={false}
              className="SlotEditForm__progress"
            />
          </div>
        </div>
      )}

      {/* Временной диапазон */}
      <div className="SlotEditForm__section-title">Временной диапазон</div>
      <div className="SlotEditForm__time-row">
        <Form.Group className="SlotEditForm__time-field">
          <Form.ControlLabel>Начало</Form.ControlLabel>
          <DatePicker
            value={formState.ts_start}
            locale={RSUITE_DATEPICKER_LOCALE}
            format="dd.MM.yyyy HH:mm"
            ranges={[]}
            cleanable={false}
            onSelect={handleTimeChange('ts_start')}
            hideMinutes={(minute) => minute % 5 !== 0}
            block
          />
        </Form.Group>
        <Form.Group className="SlotEditForm__time-field">
          <Form.ControlLabel>Окончание</Form.ControlLabel>
          <DatePicker
            value={formState.ts_end}
            locale={RSUITE_DATEPICKER_LOCALE}
            format="dd.MM.yyyy HH:mm"
            ranges={[]}
            cleanable={false}
            onSelect={handleTimeChange('ts_end')}
            hideMinutes={(minute) => minute % 5 !== 0}
            block
          />
        </Form.Group>
      </div>

      {/* Лимиты */}
      <div className="SlotEditForm__section-title">Лимиты</div>
      <div className="SlotEditForm__limits-row">
        <Form.Group className="SlotEditForm__limit-field">
          <Form.ControlLabel>Лимит заказов</Form.ControlLabel>
          <Form.Control
            accepter={InputNumber}
            min={0}
            step={1}
            name="orders_capacity"
          />
        </Form.Group>
        <Form.Group className="SlotEditForm__limit-field">
          <Form.ControlLabel>Лимит позиций</Form.ControlLabel>
          <Form.Control
            accepter={InputNumber}
            min={0}
            step={1}
            name="position_capacity"
          />
        </Form.Group>
      </div>

      <Form.Group className="mb-5">
        <Form.ControlLabel>Таймаут закрытия (минуты)</Form.ControlLabel>
        <Form.Control
          accepter={InputNumber}
          min={0}
          step={1}
          name="timeout"
        />
        <Form.HelpText>За сколько минут до начала слот перестаёт принимать заказы</Form.HelpText>
      </Form.Group>

      {/* Кнопки */}
      <ButtonToolbar className="SlotEditForm__actions">
        {onCancel && (
          <Button appearance="subtle" onClick={onCancel}>
            Отмена
          </Button>
        )}
        <Button
          appearance="primary"
          color="violet"
          type="submit"
          loading={loadingUpdateSlotInfo}
          disabled={loading || loadingUpdateSlotInfo}
        >
          Сохранить
        </Button>
      </ButtonToolbar>
    </Form>
  )
}
